import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Fuel, Settings2, Loader2, Sparkles, KeyRound, LayoutGrid, Rows3 } from 'lucide-react';
import { api, useAuthStore } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { FuelReviewDialog } from '../components/fuel/FuelReviewDialog';
import { SubCard } from '../components/fuel/SubCard';
import { SubsSummary } from '../components/fuel/SubsSummary';
import { SubsFilters } from '../components/fuel/SubsFilters';
import { Pager } from '../components/fuel/Pager';
import { SubsTable } from '../components/fuel/SubsTable';

const VIEW_KEY = 'fuel-submissions-view';

const TABS = [['pending', 'Bekleyen'], ['approved', 'Onaylanan'], ['rejected', 'Reddedilen']];

function AiSettings({ open, onClose }) {
  const isSuper = useAuthStore((s) => s.user?.role) === 'SUPER_ADMIN';
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setKey(''); api.get('/fuel-submissions/settings/ai').then((r) => setCfg(r.data.data)); } }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { mode: cfg.mode, auto_read: cfg.auto_read, ...(key ? { anthropic_key: key } : {}) };
      setCfg((await api.put('/fuel-submissions/settings/ai', body)).data.data);
      toast.success('Fiş okuma ayarları kaydedildi'); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fuel-ai-settings">
        <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" />Fiş Okuma (Yapay Zeka)</DialogTitle>
        <DialogDescription>Fişler Claude ile okunur; okunamayan alanları elle doldurursunuz.</DialogDescription>
        {!cfg ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            {[['emergent', 'Emergent anahtarı (hazır)', 'Ek ayar gerekmez, kullanım bakiyenizden düşer'], ['own', 'Kendi Claude API anahtarım', 'Anthropic hesabınızdan alınan sk-ant-… anahtarı']].map(([k, l, d]) => (
              <label key={k} className={`flex gap-2 rounded-xl border p-3 cursor-pointer ${cfg.mode === k ? 'border-violet-500 bg-violet-50/50' : 'border-slate-200'}`} data-testid={`fuel-ai-mode-${k}`}>
                <input type="radio" checked={cfg.mode === k} disabled={!isSuper} onChange={() => setCfg({ ...cfg, mode: k })} className="mt-1" />
                <span><b className="text-sm">{l}</b><span className="block text-[11px] text-slate-500">{d}</span></span>
              </label>
            ))}
            {cfg.mode === 'own' && (
              <label className="block text-sm"><span className="flex items-center gap-1 font-semibold"><KeyRound className="h-3.5 w-3.5" />Claude API anahtarı</span>
                <input type="password" value={key} disabled={!isSuper} onChange={(e) => setKey(e.target.value)} placeholder={cfg.key_masked || 'sk-ant-...'} className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="fuel-ai-key" />
                {cfg.has_own_key && <span className="text-[11px] text-emerald-700">Kayıtlı anahtar: {cfg.key_masked} (değiştirmek için yenisini yazın)</span>}
              </label>
            )}
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-sm"><span><b>Şoför gönderince otomatik oku</b><span className="block text-[11px] text-slate-500">Kapalıysa resme tıklayıp "Bu fişi oku" ile okutursunuz</span></span>
              <Switch checked={cfg.auto_read} disabled={!isSuper} onCheckedChange={(v) => setCfg({ ...cfg, auto_read: v })} data-testid="fuel-ai-auto" /></label>
            <p className="text-[11px] text-slate-500">Model: {cfg.model}</p>
          </div>
        )}
        {isSuper && <DialogFooter><Button variant="ghost" onClick={onClose}>Vazgeç</Button><Button onClick={save} disabled={busy || !cfg} data-testid="fuel-ai-save">Kaydet</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { date_from: '', date_to: '', employee_id: '', plate: '', firm_id: '', ai_status: '', card_company: '', q: '' };

export default function FuelSubmissions() {
  const [tab, setTab] = useState('pending');
  const [f, setF] = useState(EMPTY);
  const [qDeb, setQDeb] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(24);
  const [opts, setOpts] = useState({});
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [settings, setSettings] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'cards');
  useEffect(() => { const t = setTimeout(() => setQDeb(f.q), 350); return () => clearTimeout(t); }, [f.q]);
  useEffect(() => { api.get('/fuel-submissions/filter-options').then((r) => setOpts(r.data.data)).catch(() => {}); }, []);
  const set = (patch) => { setF((p) => ({ ...p, ...patch })); setPage(1); };
  const load = useCallback(() => {
    const params = { status: tab, page, page_size: size, ...Object.fromEntries(Object.entries({ ...f, q: qDeb }).filter(([, v]) => v)) };
    return api.get('/fuel-submissions', { params }).then((r) => setData(r.data.data)).catch((e) => toast.error(e?.response?.data?.detail || 'Yüklenemedi'));
  }, [tab, page, size, f.date_from, f.date_to, f.employee_id, f.plate, f.firm_id, f.ai_status, f.card_company, qDeb]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-4 pb-24" data-testid="fuel-submissions-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-orange-500 text-white flex items-center justify-center"><Fuel className="h-6 w-6" /></div>
        <div className="flex-1"><h1 className="text-2xl font-black">Mazot Bildirimleri</h1><p className="text-sm text-slate-500">Şoförlerden gelen fişleri kontrol edip onaylayın; onaylananlar Yakıt Raporu'na yazılır.</p></div>
        <Button variant="outline" onClick={() => setSettings(true)} data-testid="fuel-ai-settings-btn"><Settings2 className="h-4 w-4 mr-1" />Fiş okuma ayarları</Button>
      </div>
      <SubsSummary s={data?.summary} />
      <SubsFilters f={f} set={set} opts={opts} onReset={() => { setF(EMPTY); setPage(1); }} />
      <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-1 gap-1">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setPage(1); }} data-testid={`fuel-tab-${k}`} className={`h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${tab === k ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            {l}{data?.counts?.[k] != null && <span className="ml-1.5 text-[11px] opacity-70">{data.counts[k]}</span>}
          </button>
        ))}
      </div>
        <div className="flex rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-1 gap-1" role="group">
          {[['cards', LayoutGrid, 'Kart'], ['table', Rows3, 'Tablo']].map(([k, Icon, label]) => (
            <button key={k} type="button" onClick={() => { setView(k); localStorage.setItem(VIEW_KEY, k); }} data-testid={`fuel-view-${k}`} title={label}
              className={`h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === k ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.rows.length === 0 ? <p className="text-sm text-slate-400 py-12 text-center" data-testid="fuel-empty">Bu filtrelerle bildirim yok</p> : view === 'table' ? (
        <SubsTable rows={data.rows} onOpen={setOpenId} />
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">{data.rows.map((r) => <SubCard key={r.id} r={r} onOpen={setOpenId} />)}</div>
      )}
      {data && <Pager page={data.page} size={size} total={data.total_rows} onPage={setPage} onSize={(n) => { setSize(n); setPage(1); }} />}
      {openId && <FuelReviewDialog id={openId} onClose={() => { setOpenId(null); load(); }} onDone={() => { setOpenId(null); load(); }} />}
      <AiSettings open={settings} onClose={() => setSettings(false)} />
    </div>
  );
}
